/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Modal, ModalBody, ModalHeader } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

export interface IPromoModalProps {
    isOpen: boolean;
    heading: string;
    message: string;

    /** Optional second line under the message, such as the name of a replacement item. */
    detail?: string;
    okButtonLabel: string;

    /** Variant class, so each use of the dialog can be styled on its own. */
    className?: string;
    onClose(): void;
}

/**
 * The one dialog the promo module shows, for both failures and supersession notices.
 *
 * Both are informative and share a layout: a heading, a message, an optional detail line and
 * a single button. Every way of dismissing it calls onClose.
 * @param props - The modal props.
 * @returns The promo modal.
 */
export const PromoModal: React.FC<IPromoModalProps> = ({ isOpen, heading, message, detail, okButtonLabel, className, onClose }) => {
    const focusDelay = 110;
    const okButtonRef = React.useRef<HTMLButtonElement>(null);

    React.useEffect(() => {
        if (isOpen && okButtonRef.current) {
            setTimeout(() => {
                okButtonRef.current?.focus();
            }, focusDelay);
        }
    }, [isOpen]);

    return (
        <Modal
            className={classnames('gdn-promo-modal', className)}
            isOpen={isOpen}
            horizontalPosition='center'
            verticalPosition='center'
            autoFocus
            fade={false}
            toggle={onClose}
            aria-label={heading}
        >
            <ModalHeader tag='h2' className='gdn-promo-modal__header' toggle={onClose}>
                {heading}
            </ModalHeader>

            <ModalBody className='gdn-promo-modal__body'>
                <p className='gdn-promo-modal__message'>{message}</p>
                {detail ? <p className='gdn-promo-modal__detail'>{detail}</p> : null}
                <div className='gdn-promo-modal__actions'>
                    <button ref={okButtonRef} type='button' onClick={onClose}>
                        {okButtonLabel}
                    </button>
                </div>
            </ModalBody>
        </Modal>
    );
};

export default PromoModal;
